import { create } from "zustand";

import type { Tag } from "@app/common/types";
import { EnterpriseWorkspaceTeam } from "@app/common/workspaces/types";

import type { DropdownItem } from "@rui/Dropdown/types";

const EMPTY_SELECTED_RANGE = { value: "", label: "-" };

interface PulseStoreStateValues {
  forYou: {
    dropdownOptions: DropdownItem[] | null;
    selectedRange: DropdownItem;
  };
  forYouTags: {
    availableTags: Tag[];
    dropdownOptions: DropdownItem[] | null;
    selectedRange: DropdownItem;
    selectedTag: Tag | null;
  };
  forYourTeams: {
    availableTeams: EnterpriseWorkspaceTeam[];
    dropdownOptions: DropdownItem[] | null;
    selectedRange: DropdownItem;
    selectedTeam: EnterpriseWorkspaceTeam | null;
  };
  forYourWorkspace: {
    dropdownOptions: DropdownItem[] | null;
    selectedRange: DropdownItem;
  };
}

interface PulseStoreState extends PulseStoreStateValues {
  initializeTagResults: (tags: Tag[], dropdownOptions: DropdownItem[]) => void;
  initializeTeamsResults: (
    teams: EnterpriseWorkspaceTeam[],
    dropdownOptions: DropdownItem[]
  ) => void;
  setDropdownOptions: (
    page: "forYou" | "forYouTags" | "forYourWorkspace" | "forYourTeams",
    options: DropdownItem[]
  ) => void;
  setSelectedRange: (
    page: "forYou" | "forYouTags" | "forYourWorkspace" | "forYourTeams",
    newRange: DropdownItem
  ) => void;
  setSelectedTag: (tag: Tag) => void;
  setSelectedTeam: (tag: EnterpriseWorkspaceTeam) => void;
}

const DEFAULT_STATE: PulseStoreStateValues = {
  forYou: {
    dropdownOptions: null,
    selectedRange: EMPTY_SELECTED_RANGE,
  },
  forYouTags: {
    availableTags: [],
    dropdownOptions: null,
    selectedRange: EMPTY_SELECTED_RANGE,
    selectedTag: null,
  },
  forYourTeams: {
    availableTeams: [],
    dropdownOptions: null,
    selectedRange: EMPTY_SELECTED_RANGE,
    selectedTeam: null,
  },
  forYourWorkspace: {
    dropdownOptions: null,
    selectedRange: EMPTY_SELECTED_RANGE,
  },
};

const usePulseStore = create<PulseStoreState>((set) => ({
  ...DEFAULT_STATE,
  initializeTagResults: (availableTags, dropdownOptions) =>
    set((state) => ({
      forYouTags: {
        dropdownOptions,
        availableTags,
        selectedRange: state.forYouTags.selectedRange.value
          ? state.forYouTags.selectedRange
          : dropdownOptions[0] ?? EMPTY_SELECTED_RANGE,
        selectedTag: state.forYouTags.selectedTag
          ? state.forYouTags.selectedTag
          : availableTags[0] ?? null,
      },
    })),
  initializeTeamsResults: (availableTeams, dropdownOptions) =>
    set((state) => ({
      forYourTeams: {
        dropdownOptions,
        availableTeams,
        selectedRange: state.forYourTeams.selectedRange.value
          ? state.forYourTeams.selectedRange
          : dropdownOptions[0] ?? EMPTY_SELECTED_RANGE,
        selectedTeam: state.forYourTeams.selectedTeam
          ? state.forYourTeams.selectedTeam
          : availableTeams[0] ?? null,
      },
    })),
  setDropdownOptions: (page, options) =>
    set((state) => ({
      [page]: {
        dropdownOptions: options,
        selectedRange: state[page].selectedRange.value
          ? state[page].selectedRange
          : options[0] ?? EMPTY_SELECTED_RANGE,
      },
    })),
  setSelectedRange: (page, selected) =>
    set((state) => ({
      [page]: {
        ...state[page],
        selectedRange: selected,
      },
    })),
  setSelectedTag: (selectedTag) =>
    set((state) => ({
      forYouTags: {
        ...state.forYouTags,
        selectedTag,
      },
    })),
  setSelectedTeam: (selectedTeam) =>
    set((state) => ({
      forYourTeams: {
        ...state.forYourTeams,
        selectedTeam,
      },
    })),
}));

export default usePulseStore;
